import { AppError } from './AppError';

/**
 * Error thrown when there is an internal server error.
 */
export class InternalServerError extends AppError {
  /**
   * Creates a new InternalServerError instance.
   * @param message The error message.
   * @param originalError The original error that caused this error.
   */
  constructor(
    message: string = 'Internal server error',
    originalError?: Error
  ) {
    super(message, 'INTERNAL_SERVER_ERROR', false, { originalError });
    this.name = 'InternalServerError';

    // If there's an original error, append its stack to this error's stack
    if (originalError && originalError.stack) {
      this.stack = `${this.stack}\nCaused by: ${originalError.stack}`;
    }
  }

  /**
   * Creates an InternalServerError from an original error.
   * @param originalError The original error that caused this error.
   * @param message Optional custom message. If not provided, uses the original error's message.
   * @returns A new InternalServerError instance.
   */
  static fromError(originalError: Error, message?: string): InternalServerError {
    return new InternalServerError(
      message || `Internal server error: ${originalError.message}`,
      originalError
    );
  }

  /**
   * Creates an InternalServerError for a database error.
   * @param originalError The original database error.
   * @returns A new InternalServerError instance.
   */
  static database(originalError: Error): InternalServerError {
    return new InternalServerError(
      `Database error: ${originalError.message}`,
      originalError
    );
  }

  /**
   * Creates an InternalServerError for an external service error.
   * @param serviceName The name of the external service.
   * @param originalError The original error from the external service.
   * @returns A new InternalServerError instance.
   */
  static externalService(serviceName: string, originalError: Error): InternalServerError {
    return new InternalServerError(
      `External service error (${serviceName}): ${originalError.message}`,
      originalError
    );
  }
}